import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
`;

/* ── Left panel (form) ─────────────────────────────────────────── */
const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  background: ${p => p.theme.colors.backgroundLight};
  animation: ${fadeUp} 0.5s ease-out;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: 32px 24px;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 380px;
`;

const LogoArea = styled.div`
  margin-bottom: 36px;
`;

const LogoBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px ${p => p.theme.colors.primary}60;

  span {
    font-size: 1.4rem;
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 1.625rem;
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 6px;
  letter-spacing: -0.02em;
`;

const WelcomeSubtitle = styled.p`
  font-size: 0.875rem;
  color: ${p => p.theme.colors.textLight};
  margin: 0;
  font-weight: 400;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 14px;
  color: ${p => p.theme.colors.textLight};
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 42px 11px 40px;
  border: 1.5px solid ${p => p.hasError ? p.theme.colors.danger : p.theme.colors.border};
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: ${p => p.theme.fonts.primary};
  transition: all 0.2s ease;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};

  &:focus {
    outline: none;
    border-color: ${p => p.hasError ? p.theme.colors.danger : p.theme.colors.primary};
    background: ${p => p.theme.colors.backgroundLight};
    box-shadow: 0 0 0 3px ${p => p.hasError ? `${p.theme.colors.danger}26` : `${p.theme.colors.primary}26`};
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: ${p => p.theme.colors.textLight};
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.2s ease;

  &:hover { color: ${p => p.theme.colors.primary}; }
`;

const ErrorBox = styled.div`
  background: ${p => `${p.theme.colors.danger}1A`};
  color: ${p => p.theme.colors.danger};
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  border-left: 3px solid ${p => p.theme.colors.danger};
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;
  box-shadow: 0 4px 16px ${p => p.theme.colors.primary}60;
  letter-spacing: 0.01em;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 22px ${p => p.theme.colors.primary}80;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

/* ── Right panel (visual) ──────────────────────────────────────── */
const RightPanel = styled.div`
  width: 45%;
  background: linear-gradient(135deg, ${p => p.theme.colors.primaryDark} 0%, ${p => p.theme.colors.primary} 40%, ${p => p.theme.colors.primaryLight} 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  position: relative;
  overflow: hidden;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Wave = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

const RightContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
`;

const RightTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 14px;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const RightSubtitle = styled.p`
  font-size: 1rem;
  opacity: 0.85;
  max-width: 300px;
  margin: 0 auto 32px;
  line-height: 1.6;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255,255,255,0.15);
  border-radius: 12px;
  padding: 12px 16px;
  backdrop-filter: blur(4px);
`;

const FeatureIcon = styled.div`
  font-size: 1.25rem;
`;

const FeatureText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
`;

const CircleDecor = styled.div`
  position: absolute;
  border-radius: 50%;
  background: rgba(255,255,255,0.08);

  &.c1 { width: 300px; height: 300px; top: -80px; right: -80px; }
  &.c2 { width: 200px; height: 200px; bottom: 60px; left: -60px; }
  &.c3 { width: 120px; height: 120px; top: 40%; right: 20px; }
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      setError('Tu sesión expiró. Inicia sesión de nuevo para continuar.');
      sessionStorage.removeItem('sessionExpired');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Por favor, completa todos los campos');
      return;
    }
    if (!email.includes('@')) {
      setError('Por favor, ingresa un email válido');
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.message || 'Credenciales incorrectas. Intenta nuevamente.');
      }
    } catch {
      setError('Error al iniciar sesión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LeftPanel>
        <FormWrapper>
          <LogoArea>
            <LogoBox><span>💳</span></LogoBox>
            <WelcomeTitle>Bienvenido de nuevo</WelcomeTitle>
            <WelcomeSubtitle>Ingresa tus credenciales para continuar</WelcomeSubtitle>
          </LogoArea>

          {error && <ErrorBox style={{ marginBottom: 16 }}>{error}</ErrorBox>}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">Correo Electrónico</Label>
              <InputWrapper>
                <InputIcon><MdEmail /></InputIcon>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@empresa.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  hasError={!!error && !email}
                  autoComplete="email"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Contraseña</Label>
              <InputWrapper>
                <InputIcon><MdLock /></InputIcon>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Tu contraseña"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  hasError={!!error && !password}
                  autoComplete="current-password"
                />
                <ToggleBtn type="button" onClick={() => setShowPassword(!showPassword)} tabIndex="-1">
                  {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                </ToggleBtn>
              </InputWrapper>
            </FormGroup>

            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </SubmitButton>
          </Form>
        </FormWrapper>
      </LeftPanel>

      <RightPanel>
        <CircleDecor className="c1" />
        <CircleDecor className="c2" />
        <CircleDecor className="c3" />

        <RightContent>
          <RightTitle>Payment Manager</RightTitle>
          <RightSubtitle>
            Gestiona tus cobros, clientes y recibos desde un solo lugar.
          </RightSubtitle>

          <FeatureList>
            <FeatureItem>
              <FeatureIcon>📊</FeatureIcon>
              <FeatureText>Dashboard con métricas en tiempo real</FeatureText>
            </FeatureItem>
            <FeatureItem>
              <FeatureIcon>💬</FeatureIcon>
              <FeatureText>Cobros automáticos por WhatsApp</FeatureText>
            </FeatureItem>
            <FeatureItem>
              <FeatureIcon>🧾</FeatureIcon>
              <FeatureText>Recibos y reportes en PDF</FeatureText>
            </FeatureItem>
            <FeatureItem>
              <FeatureIcon>🔔</FeatureIcon>
              <FeatureText>Alertas de vencimiento automáticas</FeatureText>
            </FeatureItem>
          </FeatureList>
        </RightContent>

        <Wave>
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 60L48 55C96 50 192 40 288 45C384 50 480 70 576 75C672 80 768 70 864 60C960 50 1056 40 1152 42C1248 44 1344 58 1392 65L1440 72V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V60Z" fill="rgba(255,255,255,0.1)"/>
          </svg>
        </Wave>
      </RightPanel>
    </LoginContainer>
  );
};

export default Login;
